import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Search, Filter, Home, MapPin, Euro, Calendar, Eye, Edit } from 'lucide-react';

const PropertyOverview = () => {
  const [properties, setProperties] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterCommune, setFilterCommune] = useState('all');

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch communes
      const { data: communesData } = await supabase?.from('communes')?.select('id, name')?.order('name');
      
      setCommunes(communesData || []);

      // Build properties query with filters
      let query = supabase?.from('properties')?.select(`
          *,
          commune:communes(name, district),
          owner:user_profiles!properties_owner_id_fkey(full_name, email),
          agent:user_profiles!properties_agent_id_fkey(full_name, email),
          appointments(count)
        `);

      if (filterStatus !== 'all') {
        query = query?.eq('status', filterStatus);
      }

      if (filterType !== 'all') {
        query = query?.eq('property_type', filterType);
      }

      if (filterCommune !== 'all') {
        query = query?.eq('commune_id', filterCommune);
      }

      if (searchTerm) {
        query = query?.or(`title.ilike.%${searchTerm}%,address.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      
      setProperties(data || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchTerm, filterStatus, filterType, filterCommune]);

  const statusColors = {
    available: 'bg-green-100 text-green-800',
    reserved: 'bg-yellow-100 text-yellow-800',
    sold: 'bg-red-100 text-red-800',
    withdrawn: 'bg-gray-100 text-gray-800'
  };

  const statusLabels = {
    available: 'Disponible',
    reserved: 'Réservé',
    sold: 'Vendu',
    withdrawn: 'Retiré'
  };

  const typeLabels = {
    apartment: 'Appartement',
    house: 'Maison',
    land: 'Terrain',
    commercial: 'Commercial',
    villa: 'Villa'
  };

  const formatPrice = (price) => {
    if (!price) return 'Prix non défini';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    })?.format(price);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Aperçu des Propriétés</h2>
        <p className="text-sm text-gray-600">
          Vue d'ensemble des propriétés avec informations sur les communes et analyses d'investissement
        </p>
      </div>
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher propriétés..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="relative">
            <Filter className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
            >
              <option value="all">Tous les statuts</option>
              <option value="available">Disponible</option>
              <option value="reserved">Réservé</option>
              <option value="sold">Vendu</option>
              <option value="withdrawn">Retiré</option>
            </select>
          </div>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e?.target?.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tous les types</option>
            <option value="apartment">Appartement</option>
            <option value="house">Maison</option>
            <option value="land">Terrain</option>
            <option value="commercial">Commercial</option>
            <option value="villa">Villa</option>
          </select>

          <select
            value={filterCommune}
            onChange={(e) => setFilterCommune(e?.target?.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Toutes les communes</option>
            {communes?.map((commune) => (
              <option key={commune?.id} value={commune?.id}>
                {commune?.name}
              </option>
            ))}
          </select>

          <div className="text-sm text-gray-600 flex items-center">
            <span>{properties?.length} propriété(s)</span>
          </div>
        </div>
      </div>
      {/* Properties Grid */}
      {loading ? (
        <div className="bg-white p-8 rounded-lg shadow-sm border text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des propriétés...</p>
        </div>
      ) : properties?.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow-sm border text-center text-gray-500">
          <Home className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>Aucune propriété trouvée</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties?.map((property) => (
            <div key={property?.id} className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow">
              {/* Property Image */}
              <div className="h-48 bg-gray-200 relative">
                {property?.images && property?.images?.length > 0 ? (
                  <img
                    src={property?.images?.[0]}
                    alt={property?.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/assets/images/no_image.png';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Home className="h-12 w-12 text-gray-400" />
                  </div>
                )}
                <div className="absolute top-3 right-3">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors?.[property?.status] || 'bg-gray-100 text-gray-800'}`}>
                    {statusLabels?.[property?.status] || property?.status}
                  </span>
                </div>
              </div>

              {/* Property Info */}
              <div className="p-4">
                <div className="mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                    {property?.title}
                  </h3>
                  <div className="flex items-center text-sm text-gray-600 mt-1">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span className="truncate">{property?.address}</span>
                  </div>
                  {property?.commune && (
                    <div className="text-sm text-blue-600 mt-1">
                      {property?.commune?.name}, {property?.commune?.district}
                    </div>
                  )}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Type:</span>
                    <span className="font-medium">{typeLabels?.[property?.property_type] || property?.property_type}</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Prix:</span>
                    <span className="font-bold text-green-600 flex items-center">
                      <Euro className="h-4 w-4 mr-1" />
                      {formatPrice(property?.price)}
                    </span>
                  </div>

                  {property?.surface_area && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Surface:</span>
                      <span className="font-medium">{property?.surface_area} m²</span>
                    </div>
                  )}

                  {property?.bedrooms && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Chambres:</span>
                      <span className="font-medium">{property?.bedrooms}</span>
                    </div>
                  )}
                </div>

                {/* Owner and Agent Info */}
                <div className="space-y-2 mb-4 text-sm">
                  {property?.owner && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Propriétaire:</span>
                      <span className="font-medium truncate ml-2">{property?.owner?.full_name}</span>
                    </div>
                  )}
                  
                  {property?.agent && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Agent:</span>
                      <span className="font-medium truncate ml-2">{property?.agent?.full_name}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">RDV:</span>
                    <span className="font-medium">{property?.appointments?.[0]?.count || 0}</span>
                  </div>
                </div>

                {/* Property Features */}
                {property?.features && Object.keys(property?.features)?.length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(property?.features)?.slice(0, 3)?.map(([key, value]) => (
                        value && (
                          <span key={key} className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                            {key === 'pool' ? 'Piscine' :
                             key === 'garden' ? 'Jardin' :
                             key === 'parking' ? 'Parking' :
                             key === 'security' ? 'Sécurité' :
                             key === 'furnished' ? 'Meublé' :
                             key}
                          </span>
                        )
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex space-x-2 pt-3 border-t">
                  <button className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-blue-600 hover:text-blue-800 text-sm">
                    <Eye className="h-4 w-4" />
                    <span>Voir</span>
                  </button>
                  <button className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-gray-600 hover:text-gray-800 text-sm">
                    <Edit className="h-4 w-4" />
                    <span>Modifier</span>
                  </button>
                </div>

                {/* Creation Date */}
                <div className="mt-3 pt-3 border-t text-xs text-gray-500 flex items-center">
                  <Calendar className="h-3 w-3 mr-1" />
                  Ajoutée le {new Date(property?.created_at)?.toLocaleDateString('fr-FR')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PropertyOverview;